import { Measurement } from './Measurement';
import { Nutrient } from './Nutrient';
import { NutrientSource } from '../NutrientSource/NutrientSource';
import { IngredientMeasurementEnum } from './IngredientMeasurementEnum';

type NutrientKey = "energy" | "fat" | "saturatedFattyAcids" | "transFattyAcids" |
  "cholesterol" | "sodium" | "carbohydrate" | "fibre" | "sugars" | "protein" |
  "vitaminD" | "vitaminC" | "potassium" | "calcium" | "iron" | "addedSugars" |
  "sugarAlcohols" | "monounsaturatedFats" | "polyunsaturatedFats" | "omega3" | "omega6" |
  "caffeine" | "ash" | "water" | "moisture" |
  // sugar alcohols
  "erythritol" | "isomalt" | "mannitol" | "xylitol" | "lactitol" |
  "sorbitol" | "maltitol" | "unspecifiedSugarAlcohol";

type IngredientNutrientMap = { [key in NutrientKey]: Nutrient };

export type Ingredient = {
    id: number;
    foodCode:string;
    name: string;
    weightInGrams: number;
    allMeasurements: Measurement[];
    unitOfMeasurement: IngredientMeasurementEnum;
    quantityInUnitOfMeasurement: number;
    hasVolumeUnitOfMeasurement: Boolean;

    additionalNutrients: Nutrient[];

    // energy: |
    // fat: |

    nutrientSource?: NutrientSource | null;
    nutrients: any[];

    brand: string | null;
    components: string | null;

    measurement?: Measurement;

    categoryDensityId: number|null;
    subIngredients?: string;

    createdOn: Date | null;
    updatedOn: Date | null;
    createdUserName: string | null;
    updatedUserName: string | null;
} & IngredientNutrientMap;

/**
 * Creates a new ingredient with all properties set to their default values
 * TODO: Allow v-ingredient-nutrient-quantity-input-row to handle the default values instead
 *
 * @returns An object representing the default ingredient state
 */
export function createDefaultIngredient(): Ingredient {
  return {
    id: 0,
    foodCode: "",
    name: "",
    brand: "",
    components: "",
    energy: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    fat: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    saturatedFattyAcids: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    transFattyAcids: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    cholesterol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    sodium: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    carbohydrate: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    fibre: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    sugars: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    protein: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    vitaminD: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    vitaminC: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    potassium: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    calcium: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    iron: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    addedSugars: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    sugarAlcohols: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    monounsaturatedFats: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    polyunsaturatedFats: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    omega3: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    omega6: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    caffeine: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    ash: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    water: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    moisture: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0
    },
    erythritol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    isomalt: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    mannitol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    xylitol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    lactitol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    sorbitol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    maltitol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    unspecifiedSugarAlcohol: {
      id: 0,
      value: 0,
      nutrientUnitId: 0,
      nutrientUnit: null,
      ingredientId: 0,
    },
    allMeasurements: [],
    additionalNutrients: [],
    weightInGrams: 100,
    nutrients: [],
    unitOfMeasurement: 1,
    quantityInUnitOfMeasurement: 100,
    hasVolumeUnitOfMeasurement: false,
    categoryDensityId: null,
    createdOn: null,
    updatedOn: null,
    createdUserName: null,
    updatedUserName: null,
  };
}